// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX, FiUser } from 'react-icons/fi';
import ZenQBlogo from '../logo192.png';
import { trackEvent } from '../utils/analytics';
const Navbar = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleLogout = () => {
    trackEvent("logout_clicked", { source: "navbar" });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const handleNavigate = (item) => {
    trackEvent("nav_click", { label: item.label, path: item.path });
    navigate(item.path);
  };

  const isActive = (itemPath) => {
    if (itemPath.includes('?')) return `${location.pathname}${location.search}` === itemPath;
    return location.pathname === itemPath;
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'School', path: '/mode?category=school' },
    { label: 'College', path: '/mode?category=college' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={navWrapper}
    >
      <div style={navContainer}>
        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => navigate('/')}>
          <img src={ZenQBlogo} alt="ZenQB Logo" style={{ height: '36px', transition: 'transform 0.2s ease' }}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        </div>
        {isMobile && (
          <div onClick={toggleMenu} className="hamburger-icon">
            {menuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
          </div>
        )}

        {isMobile ? (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: menuOpen ? 0 : '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={sideDrawerStyle}
          >
            {[...menuItems, user ? { label: 'Logout', path: '/logout' } : { label: 'Login', path: '/login' }].map(
              (item) => (
                <div
                  key={item.path}
                  onClick={() => {
                    if (item.path === '/logout') handleLogout();
                    else handleNavigate(item);
                    setMenuOpen(false);
                  }}
                  style={{
                    padding: '15px 10px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    color: isActive(item.path) ? '#6366f1' : '#374151',
                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                  }}
                >
                  {item.label}
                </div>
              )
            )}
          </motion.div>
        ) : (
          <ul style={{ ...navListStyle, display: 'flex' }}>
            {menuItems.map((item) => (
              <motion.li
                key={item.path}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNavigate(item)}
                style={{
                  ...navItemStyle,
                  color: isActive(item.path) ? '#6366f1' : '#374151',
                  fontWeight: isActive(item.path) ? 'bold' : 'normal',
                }}
              >
                {item.label}
              </motion.li>
            ))}
            {user ? (
              <>
                <li style={{ ...navItemStyle, color: '#6366f1' }}>
                  <FiUser style={{ marginRight: 5 }} />
                  {user.email.split("@")[0]}
                </li>
                <motion.li
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  style={{ ...navItemStyle, color: '#ef4444', fontWeight: 'bold' }}
                >
                  Logout
                </motion.li>
              </>
            ) : (
              <motion.li
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  trackEvent("nav_click", { label: "Login", path: "/login" });
                  navigate('/login');
                }}
                style={{
                  ...navItemStyle,
                  color: '#6366f1',
                  fontWeight: location.pathname === '/login' ? 'bold' : 'normal',
                }}
              >
                Login
              </motion.li>
            )}
          </ul>
        )}
      </div>
    </motion.nav>
  );
};

const navWrapper = {
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  padding: '1rem 0',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  position: 'sticky',
  top: 0,
  zIndex: 999,
  width: '100%',
  borderBottom: '1px solid #f3f4f6',
};

const navContainer = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 1rem',
  width: '100%',
};

const navListStyle = {
  listStyle: 'none',
  gap: '20px',
  margin: 0,
  padding: 0,
  alignItems: 'center',
  display: 'flex',
};

const navItemStyle = {
  cursor: 'pointer',
  fontSize: '16px',
  padding: '10px 20px',
  transition: 'color 0.3s',
};

const sideDrawerStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100vh',
  width: '250px',
  backgroundColor: '#ffffff',
  boxShadow: '-2px 0 20px rgba(0, 0, 0, 0.08)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '4rem',
};

export default Navbar;
